import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/home/card';
import { Button } from '../../components/home/buttons';

type BugReportsLocationState = {
    from?: {
        pathname?: string;
    };
};

function buildDefaultTemplate(templateBody: string, pageLabel: string, userAgentLabel: string) {
    const lines = templateBody.split('\n');

    // These are helpful when debugging and are included only in the user-copied text.
    if (typeof window !== 'undefined') {
        lines.push(`${pageLabel}: ${window.location.href}`);
    }
    if (typeof navigator !== 'undefined') {
        lines.push(`${userAgentLabel}: ${navigator.userAgent}`);
    }

    return lines.join('\n');
}

export function BugReportsFeaturesRoute() {
    const { t } = useTranslation();
    const location = useLocation();
    const fromPath = (location.state as BugReportsLocationState | null)?.from?.pathname;

    const defaultTemplate = useMemo(
        () => buildDefaultTemplate(
            t('route.bugReportsFeatures.txtTemplateBody'),
            t('route.bugReportsFeatures.txtTemplatePageLabel'),
            t('route.bugReportsFeatures.txtTemplateUserAgentLabel')
        ),
        [t]
    );
    const [draft, setDraft] = useState(defaultTemplate);
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(draft);
            setCopyState('copied');
            window.setTimeout(() => setCopyState('idle'), 1800);
        } catch {
            setCopyState('failed');
        }
    }

    const backTo = typeof fromPath === 'string' && fromPath.length > 0 ? fromPath : '/';

    return (
        <div className="mx-auto w-full max-w-3xl">
            <Card className="animate-fade-up">
                <header className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">
                        {t('route.bugReportsFeatures.txtEyebrow')}
                    </p>
                    <h1 className="text-3xl font-semibold leading-tight text-foreground md:text-4xl">
                        {t('route.bugReportsFeatures.txtTitle')}
                    </h1>
                    <p className="text-sm text-secondary">
                        {t('route.bugReportsFeatures.txtSubtitle')}
                    </p>
                </header>

                <div className="mt-6 grid gap-6 md:grid-cols-5">
                    <section className="space-y-3 md:col-span-2">
                        <h2 className="text-base font-semibold tracking-tight text-foreground">{t('route.bugReportsFeatures.txtWhatToIncludeTitle')}</h2>
                        <ul className="list-disc space-y-2 pl-5 text-sm text-secondary">
                            <li>{t('route.bugReportsFeatures.txtChecklistExpectedVsActual')}</li>
                            <li>{t('route.bugReportsFeatures.txtChecklistSteps')}</li>
                            <li>{t('route.bugReportsFeatures.txtChecklistBrowserOs')}</li>
                            <li>{t('route.bugReportsFeatures.txtChecklistJobId')}</li>
                        </ul>
                        <div className="rounded-2xl border border-foreground/10 bg-white/80 p-4 shadow-[0_18px_30px_-26px_rgba(40,30,80,0.4)]">
                            <p className="text-sm font-semibold text-foreground">{t('route.bugReportsFeatures.txtTipTitle')}</p>
                            <p className="mt-1 text-sm text-secondary">
                                {t('route.bugReportsFeatures.txtTipBody')}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link
                                to={backTo}
                                className="inline-flex items-center justify-center rounded-full border border-foreground/10 bg-white/70 px-5 py-2 text-sm font-semibold text-foreground shadow-[0_14px_28px_-22px_rgba(40,30,80,0.35)] transition hover:-translate-y-0.5 hover:bg-white/90 active:translate-y-0"
                            >
                                {t('global.action.txtBack')}
                            </Link>
                            <Link
                                to="/privacy-policy"
                                className="text-sm font-semibold text-secondary underline decoration-foreground/20 underline-offset-4 hover:text-foreground"
                            >
                                {t('global.footer.linkPrivacyPolicy')}
                            </Link>
                        </div>
                    </section>

                    <section className="space-y-3 md:col-span-3">
                        <h2 className="text-base font-semibold tracking-tight text-foreground">{t('route.bugReportsFeatures.txtTemplateTitle')}</h2>
                        <textarea
                            value={draft}
                            onChange={(event) => {
                                setDraft(event.target.value);
                                setCopyState('idle');
                            }}
                            spellCheck={false}
                            className="h-72 w-full resize-none rounded-2xl border border-foreground/10 bg-white/80 p-4 font-mono text-xs leading-relaxed text-foreground shadow-[0_18px_30px_-26px_rgba(40,30,80,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                        />
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <Button variant="secondary" onClick={handleCopy} className="w-full sm:w-auto">
                                {copyState === 'copied'
                                    ? t('route.bugReportsFeatures.txtCopyActionCopied')
                                    : copyState === 'failed'
                                        ? t('route.bugReportsFeatures.txtCopyActionFailed')
                                        : t('route.bugReportsFeatures.txtCopyActionIdle')}
                            </Button>
                            <p className="text-xs text-secondary">
                                {copyState === 'failed'
                                    ? t('route.bugReportsFeatures.txtCopyHelpFailed')
                                    : t('route.bugReportsFeatures.txtCopyHelpIdle')}
                            </p>
                        </div>
                    </section>
                </div>
            </Card>
        </div>
    );
}
